import { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import api from '../../../../Component/axios_Interceptor/api';
import { SERVER_ADDRESS } from '../../../../Component/server';
import ConfirmModal from '../../../../Component/UI/ConfirmModal';
import ProductThumbnail from '../../../../Component/UI/ProductThumbnail';
import { mediaUrl } from '../../../../Component/tool/mediaUrl';
import BuyerFeatureIcon from '../../../../Component/UI/BuyerFeatureIcon';

type StoreSetting = { store_seq: number; store_name: string; profile_img?: string | null; live_start_enabled: boolean; statement_enabled: boolean };
type SettingField = 'live_start_enabled' | 'statement_enabled';
type Settings = { channel_blocked: boolean; stores: StoreSetting[] };
const endpoint = `${SERVER_ADDRESS}notification-settings`;
const validSettings = (value: any): value is Settings => value && typeof value.channel_blocked === 'boolean'
  && Array.isArray(value.stores) && value.stores.every((store: any) => store
    && Number.isSafeInteger(store.store_seq) && store.store_seq > 0 && typeof store.store_name === 'string'
    && (store.profile_img == null || typeof store.profile_img === 'string')
    && typeof store.live_start_enabled === 'boolean' && typeof store.statement_enabled === 'boolean');

export default function NotificationSettings() {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [reload, setReload] = useState(0);
  const [pending, setPending] = useState<{ store: StoreSetting; field: SettingField } | null>(null);
  const alive = useRef(false);
  const lock = useRef(false);

  useEffect(() => {
    alive.current = true;
    const controller = new AbortController();
    setLoading(true); setError(''); setMessage('');
    api.get(endpoint, { signal: controller.signal }).then(({ data }) => {
      if (controller.signal.aborted) return;
      if (!validSettings(data)) throw new Error('Invalid notification settings');
      setSettings(data);
    }).catch(() => {
      if (!controller.signal.aborted) setError('알림 설정을 불러오지 못했습니다. 다시 시도해 주세요.');
    }).finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => { alive.current = false; controller.abort(); };
  }, [reload]);

  useEffect(() => {
    const refresh = () => {
      if (document.visibilityState !== 'visible' || lock.current) return;
      setPending(null);
      setReload(value => value + 1);
    };
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, []);

  const save = async (store: StoreSetting, field: SettingField) => {
    if (!settings || lock.current || settings.channel_blocked) return;
    const key = `${field}-${store.store_seq}`;
    const input = { store_seq: store.store_seq, [field]: !store[field] };
    lock.current = true; setSaving(key); setError(''); setMessage('');
    try {
      const { data } = await api.patch(endpoint, input, { headers: { 'Content-Type': 'application/json' } });
      if (!alive.current) return;
      if (!validSettings(data)) throw new Error('Invalid notification settings');
      const enabled = data.stores.find(item => item.store_seq === store.store_seq)?.[field];
      if (enabled === undefined) throw new Error('Missing store notification setting');
      setSettings(data);
      setMessage(`${store.store_name} ${field === 'live_start_enabled' ? '라이브 시작' : '정산서'} 알림을 ${enabled ? '켰습니다' : '껐습니다'}.`);
    } catch {
      if (alive.current) setError('변경 결과를 확인하지 못했습니다. 다시 불러와 현재 설정을 확인해 주세요.');
    } finally { lock.current = false; if (alive.current) setSaving(null); }
  };

  const toggle = (store: StoreSetting, field: SettingField) => {
    if (!settings || lock.current || pending || settings.channel_blocked || error) return;
    if (store[field]) void save(store, field);
    else setPending({ store, field });
  };

  const control = (label: string, enabled: boolean, key: string, onClick: () => void, descriptionId: string) =>
    <Toggle type="button" role="switch" aria-label={label} aria-checked={enabled} aria-describedby={descriptionId}
      disabled={!!saving || !!pending || settings!.channel_blocked || !!error} onClick={onClick}>
      <SwitchTrack $on={enabled} aria-hidden="true"><span /></SwitchTrack>
      <span>{saving === key ? '저장 중' : enabled ? '켜짐' : '꺼짐'}</span>
    </Toggle>;

  return <SettingsPanel aria-busy={loading || !!saving}>
    {loading ? <Notice role="status">알림 설정을 불러오는 중입니다.</Notice> : settings && <>
      {settings.channel_blocked && <Notice>카카오톡 채널을 차단해 알림 수신이 꺼져 있습니다. 채널 차단을 해제한 뒤 원하는 알림을 다시 켜 주세요.</Notice>}
      <StoreSection aria-label="스토어별 알림">
        {settings.stores.length ? <SettingsList>{settings.stores.map(store => <SettingRow key={store.store_seq} aria-labelledby={`notification-store-${store.store_seq}`}>
          <StoreHeading>
            <StoreLogo src={mediaUrl(store.profile_img)} alt={`${store.store_name} 로고`} />
            <SettingText><h3 id={`notification-store-${store.store_seq}`}>{store.store_name}</h3></SettingText>
          </StoreHeading>
          <Switches>
            <SwitchLabel><span className="setting-label"><BuyerFeatureIcon name="live" />라이브 시작</span>{control(`${store.store_name} 라이브 시작 알림`, store.live_start_enabled, `live_start_enabled-${store.store_seq}`, () => toggle(store, 'live_start_enabled'), `notification-store-${store.store_seq}`)}</SwitchLabel>
            <SwitchLabel><span className="setting-label"><BuyerFeatureIcon name="receipt" />정산서</span>{control(`${store.store_name} 정산서 알림`, store.statement_enabled, `statement_enabled-${store.store_seq}`, () => toggle(store, 'statement_enabled'), `notification-store-${store.store_seq}`)}</SwitchLabel>
          </Switches>
        </SettingRow>)}</SettingsList> : <Notice>팔로우한 스토어가 없습니다. 스토어를 팔로우하면 라이브 시작·정산서 알림을 설정할 수 있어요.</Notice>}
      </StoreSection>
    </>}
    {error && <Notice role="alert">{error}<Retry type="button" onClick={() => setReload(value => value + 1)}>다시 불러오기</Retry></Notice>}
    <Feedback role="status" aria-live="polite">{message}</Feedback>
    <ConfirmModal visible={!!pending} title="알림을 켤까요?"
      subtitle="카카오톡에서 알림톡을 차단했다면 먼저 ‘알림톡 받기’로 변경해 주세요."
      confirmText="알림 켜기" onCancel={() => setPending(null)}
      onConfirm={() => { if (pending) { const selected = pending; setPending(null); void save(selected.store, selected.field); } }} />
  </SettingsPanel>;
}

const SettingsPanel = styled.section`display:flex;flex-direction:column;gap:24px;color:var(--user-text);`;
const SettingsList = styled.div`display:flex;flex-direction:column;gap:16px;`;
const SettingRow = styled.section`padding:20px;border:1px solid var(--user-border);border-radius:16px;background:var(--user-surface);@media(max-width:360px){padding:16px;}`;
const StoreHeading = styled.div`display:flex;align-items:center;gap:12px;padding-bottom:16px;border-bottom:1px solid var(--user-border);`;
const StoreLogo = styled(ProductThumbnail)`width:48px;height:48px;flex-shrink:0;border:1px solid var(--user-border);border-radius:12px;`;
const SettingText = styled.div`flex:1;min-width:0;h3{margin:0;font:17px var(--font-Pretendard-SemiBold);line-height:1.5;overflow-wrap:anywhere;}`;
const Toggle = styled.button`
  flex-shrink:0;display:flex;align-items:center;justify-content:flex-end;gap:8px;
  min-width:88px;min-height:48px;padding:0 4px;border:0;border-radius:6px;background:transparent;color:var(--user-text);font:13px var(--font-Pretendard-Medium);cursor:pointer;
  &:hover:not(:disabled){background:var(--user-surface-raised);} &:focus-visible{outline:2px solid var(--user-accent);outline-offset:4px;}
  &:disabled{cursor:default;color:var(--user-text-muted);}
  &[aria-checked='true']{color:var(--user-accent);}
`;
const SwitchTrack = styled.span<{ $on: boolean }>`
  display:flex;align-items:center;box-sizing:border-box;width:42px;height:26px;padding:3px;border:1px solid var(--user-border);border-radius:20px;
  background:${props => props.$on ? 'var(--user-button)' : 'var(--user-surface-raised)'};
  span{display:block;width:18px;height:18px;border-radius:50%;background:${props => props.$on ? '#fff' : 'var(--user-text-muted)'};transform:translateX(${props => props.$on ? '16px' : '0'});}
`;
const Notice = styled.p`margin:0;font-size:13px;line-height:1.6;color:var(--user-text-muted);&[role='alert']{color:var(--user-error);}`;
const Retry = styled.button`display:block;min-height:44px;margin-top:12px;padding:8px 12px;border:1px solid var(--user-border);border-radius:6px;background:var(--user-surface-raised);color:var(--user-text);font:inherit;cursor:pointer;&:focus-visible{outline:2px solid var(--user-accent);outline-offset:2px;}`;
const Feedback = styled.p`margin:0;font-size:13px;line-height:1.6;color:var(--user-text-muted);&:empty{display:none;}`;

const StoreSection = styled.section`display:flex;flex-direction:column;gap:16px;`;

const Switches = styled.div`display:flex;flex-direction:column;`;
const SwitchLabel = styled.div`
  display:flex;justify-content:space-between;align-items:center;gap:12px;min-height:68px;font-size:15px;
  .setting-label{display:flex;align-items:center;gap:10px;min-width:0;word-break:keep-all;}
  .setting-label svg{flex-shrink:0;color:var(--user-accent);}
  &+&{border-top:1px solid var(--user-border);}
`;
